import { motion } from "framer-motion";
import { Check } from "lucide-react";

const tiers = [
  {
    name: "Basic Wash",
    price: "$4",
    features: ["Exterior wash", "Standard dry", "Quick turnaround"],
    highlight: false,
  },
  {
    name: "Mid-Tier Wash",
    price: "$5",
    features: ["Exterior + interior vacuum", "Tire shine", "Dashboard wipe"],
    highlight: false,
  },
  {
    name: "Premium Wash",
    price: "$6",
    features: ["Full detail wash", "Interior deep clean", "Wax & polish"],
    highlight: true,
  },
];

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function WhatYouEarnSection() {
  return (
    <section
      id="earnings"
      data-testid="what-you-earn-section"
      className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500 mb-4 block">
            Transparent Payouts
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
            What You Earn
          </h2>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
        >
          {tiers.map((tier, i) => (
            <motion.div
              key={i}
              variants={item}
              data-testid={`earn-card-${tier.name.toLowerCase().replace(/\s/g, "-")}`}
              className={
                tier.highlight
                  ? "bg-neutral-900 border border-blue-500/50 rounded-2xl p-8 relative overflow-hidden shadow-[0_0_40px_rgba(37,99,235,0.1)]"
                  : "bg-neutral-900 border border-neutral-800 rounded-2xl p-8 hover:border-neutral-700 transition-colors duration-300"
              }
            >
              {tier.highlight && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-blue-400" />
              )}
              <p className="text-neutral-500 text-sm font-semibold uppercase tracking-wider mb-3">
                {tier.name}
              </p>
              <p className="text-6xl font-black text-white tracking-tighter mb-6">
                {tier.price}
              </p>
              <p className="text-neutral-500 text-sm mb-6">per wash</p>
              <div className="space-y-3">
                {tier.features.map((f, j) => (
                  <div key={j} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <span className="text-neutral-400 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex flex-wrap justify-center gap-8 mt-12"
        >
          {["Paid Weekly", "No Upfront Costs", "No Contracts"].map((tag) => (
            <span
              key={tag}
              data-testid={`earn-tag-${tag.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-bold text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded-full px-5 py-2"
            >
              {tag}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
